import moment from 'moment-timezone';

import AvailabilitySlot from './availability-slot';

// a single day's worth of availability
export default class AvailabilityDay {

  static get defaults() {
    return {
      // IANA timezone
      timezone: 'America/New_York'
    };
  }

  /**
   * AvailabilityDay constructor
   * @method constructor
   * @param  {Date}    date – day that this object represents
   * @return {AvailabilityDay}
   */

  constructor( date, opts = {} ) {
    this._options = Object.assign( {}, AvailabilityDay.defaults, opts );
    // zeroed-out date (should start at a day boundary)
    this._date = new Date( date );
    // availability slots
    this._slots = [];
    // are we loading?
    this._loading = false;
    // are we loaded?
    this._loaded = false;
    // did we error out
    this._errored = false;
    // are we pending?
    this._promise = new Promise( ( resolve, reject ) => {
      this._resolve = resolve;
      this._reject = reject;
    });
  }

  // return a copy of this._date
  get date() {
    return new Date( this._date );
  }

  // alias for `date`
  get start() {
    return this.date;
  }

  // Date at which this day ends
  get finish() {
    return new Date( this.date.getTime() + 1000 * 60 * 60 * 24 );
  }

  // prety day
  get prettyDay() {
    const date = moment( this.start ).tz( this._options.timezone );
    return date.format('dddd');
  }

  // prety date
  get prettyDate() {
    const date = moment( this.start ).tz( this._options.timezone );
    return date.format('MMM D');
  }

  // get a copy of `this._slots`
  get slots() {
    return this._slots.slice( 0 );
  }

  // get this day's cache key
  get key() {
    const tz      = this._options.timezone;
    const rounded = moment.tz( moment( this.date ).format('YYYY-MM-DD'), tz );

    return rounded.format('YYYY-MM-DD');
  }

  /**
   * Add slots (if they're within the day's bounds)
   * @method maybeAddSlots
   * @param  {Object}    availability – { slots: [], duration }
   * @return {undefined}
   */

  maybeAddSlots({ slots, duration }) {

    for ( const slot of slots ) {
      const start  = new Date( slot[ 0 ] );
      const finish = new Date( slot[ 1 ] );

      if ( start >= this.start && start < this.finish ) {
        const opts = Object.assign( {}, this._options );
        this._slots.push(
          new AvailabilitySlot( start, finish, duration, opts )
        );
      }
    }
    this._slots.sort( ( a, b ) => a.start - b.start );
  }

  /**
   * Is this day loading?
   * @method isLoading
   * @return {Boolean}
   */

  isLoading() {
    return this._loading === true;
  }

  /**
   * Is this day loaded?
   * @method isLoaded
   * @return {Boolean}
   */

  isLoaded() {
    return this._loaded === true;
  }

  /**
   * Did we encounter an error fetching this day?
   * @method isErrored
   * @return {Boolean}
   */

  isErrored() {
    return this._errored === true;
  }

  /**
   * Mark as loading
   * @method setLoading
   */

  setLoading() {
    console.log( 'LOADING: %s', this.key );
    this._loading = true;
    this._loaded = false;
    this._errored = false;
  }

  /**
   * Mark as loaded
   * @method setLoaded
   */

  setLoaded() {
    console.log( 'LOADED: %s', this.key );
    this._loading = false;
    this._loaded = true;
    this._errored = false;
    this._resolve( this.slots );
  }

  /**
   * Mark as errored
   * @method setErrored
   */

  setErrored( err ) {
    this._loading = false;
    this._loaded = false;
    this._errored = true;
    this._reject( err );
  }

  /**
   * Reset state
   * @method reset
   */

  reset() {
    this._slots.length = 0;
    this._loading = false;
    this._loaded = false;
    this._errored = false;
    this._promise = new Promise( ( resolve, reject ) => {
      this._resolve = resolve;
      this._reject = reject;
    });
  }

}
